import { tradingDayOf } from './report-groups'
import type { SalesRegisterRow } from './types'

/**
 * What a block of invoice lines is filed under, above the item — the legacy
 * "details" reports, which the server's invoice-lines workbook is cut by.
 */
export const LineBlock = {
  Category: 'category',
  Location: 'location',
  Employee: 'employee',
  Customer: 'customer',
} as const

export type LineBlock = (typeof LineBlock)[keyof typeof LineBlock]

/** Money added in whole cents, so a total is never a float-drift away from its rows. */
const cents = (amount: number | null | undefined) => Math.round((amount ?? 0) * 100)

/* -------------------------------------------------------------------------- */
/* Sales by channel                                                           */
/* -------------------------------------------------------------------------- */

export interface ChannelWeek {
  /** The Monday the week starts on, `YYYY-MM-DD`. Null on the total row. */
  week: string | null
  posInvoices: number
  posNet: number
  qrInvoices: number
  qrNet: number
  invoices: number
  net: number
  /** QR ordering's share of net, 0–1. */
  qrShare: number
}

const POS_CHANNEL = 'POS'

/** The Monday of the week a trading day falls in. */
export function weekStart(day: string): string {
  const [year, month, date] = day.split('-').map(Number)
  const at = new Date(Date.UTC(year, month - 1, date))
  const sinceMonday = (at.getUTCDay() + 6) % 7

  at.setUTCDate(at.getUTCDate() - sinceMonday)

  return at.toISOString().slice(0, 10)
}

interface ChannelTally {
  posInvoices: number
  posNet: number
  qrInvoices: number
  qrNet: number
}

function asWeek(week: string | null, tally: ChannelTally): ChannelWeek {
  const net = tally.posNet + tally.qrNet

  return {
    week,
    posInvoices: tally.posInvoices,
    posNet: tally.posNet / 100,
    qrInvoices: tally.qrInvoices,
    qrNet: tally.qrNet / 100,
    invoices: tally.posInvoices + tally.qrInvoices,
    net: net / 100,
    qrShare: net ? tally.qrNet / net : 0,
  }
}

/**
 * The register's sales laid out by week, POS beside QR ordering.
 *
 * Refunds are left out, as the server leaves them out of every sale total, so
 * the total row agrees with the sales summary for the same period.
 */
export function channelWeeks(rows: SalesRegisterRow[]): {
  weeks: ChannelWeek[]
  total: ChannelWeek
} {
  const weeks = new Map<string, ChannelTally>()
  const all: ChannelTally = { posInvoices: 0, posNet: 0, qrInvoices: 0, qrNet: 0 }

  for (const row of rows) {
    if (row.kind !== 'sale') continue

    const week = weekStart(tradingDayOf(row.at))
    const tally = weeks.get(week) ?? { posInvoices: 0, posNet: 0, qrInvoices: 0, qrNet: 0 }
    const isPos = row.channel === POS_CHANNEL

    for (const t of [tally, all]) {
      if (isPos) {
        t.posInvoices += 1
        t.posNet += cents(row.net)
      } else {
        t.qrInvoices += 1
        t.qrNet += cents(row.net)
      }
    }

    weeks.set(week, tally)
  }

  return {
    weeks: [...weeks.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([week, tally]) => asWeek(week, tally)),
    total: asWeek(null, all),
  }
}
